import { Share2, Link2, MessageCircle as WhatsAppIcon } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

function XIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}
function FacebookIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.313 0 2.686.236 2.686.236v2.97H15.83c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
    </svg>
  );
}

export function shareTargets(url, text) {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(text || "");
  return {
    x: `https://twitter.com/intent/tweet?text=${t}&url=${u}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text ? text + " " : ""}${url}`)}`,
  };
}

// Compact share menu: copy link + X / Facebook / WhatsApp.
export default function ShareMenu({ url, text, copyLabel = "Copy link", copySuccess = "Link copied", testId = "share", className = "" }) {
  const targets = shareTargets(url, text);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(copySuccess);
    } catch {
      window.prompt("Copy this link:", url);
    }
  }

  function openShare(href) {
    window.open(href, "_blank", "noopener,noreferrer,width=640,height=560");
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`h-10 w-10 rounded-xl border border-zinc-200 text-zinc-600 hover:bg-violet-50 hover:text-violet-700 hover:border-violet-200 flex items-center justify-center ${className}`}
          aria-label="Share"
          title="Share"
          data-testid={`${testId}-btn`}
        >
          <Share2 size={16} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 rounded-xl" data-testid={`${testId}-menu`}>
        <DropdownMenuItem onClick={copy} className="gap-2 cursor-pointer" data-testid={`${testId}-copy`}>
          <Link2 size={14} className="text-zinc-500" /> {copyLabel}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => openShare(targets.x)} className="gap-2 cursor-pointer" data-testid={`${testId}-x`}>
          <XIcon /> Share on X
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => openShare(targets.facebook)} className="gap-2 cursor-pointer" data-testid={`${testId}-facebook`}>
          <FacebookIcon /> <span className="text-[#1877F2] font-medium">Share on Facebook</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => openShare(targets.whatsapp)} className="gap-2 cursor-pointer" data-testid={`${testId}-whatsapp`}>
          <WhatsAppIcon size={14} className="text-[#25D366]" /> <span className="text-[#128C7E] font-medium">Share on WhatsApp</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
